import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getModule, updateModule,
  addQuestion, updateQuestion, deleteQuestion,
  deleteResource, renameResource, addLinkResource,
  deleteVideo, renameVideo,
  uploadFile, getUploadSettings,
} from '../../../api/adminApi';
import Toast from '../../../components/admin/Toast';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

async function downloadFile(filePath, fileName) {
  try {
    const token = sessionStorage.getItem('af_token');
    const res = await fetch(`/api/admin/uploads/${filePath}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { alert('File not found.'); return; }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
  } catch {
    alert('Could not download file.');
  }
}

function UploadProgress({ label, pct, icon }) {
  return (
    <div style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 14 }}>{label}</div>
      <div style={{ background: 'var(--border)', borderRadius: 4, height: 6, overflow: 'hidden', maxWidth: 320, margin: '0 auto' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--yellow)', borderRadius: 4, transition: 'width 0.2s' }} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>{pct}%</div>
    </div>
  );
}

const TABS = [
  { key: 'overview',     icon: '◎', label: 'Overview' },
  { key: 'video',        icon: '▶', label: 'Video' },
  { key: 'presentation', icon: '⊟', label: 'Slides' },
  { key: 'quiz',         icon: '✎', label: 'Quiz Builder' },
  { key: 'resources',    icon: '⊕', label: 'Resources' },
];

export default function AdminModuleEditor() {
  const { id: courseId, moduleId } = useParams();
  const navigate = useNavigate();

  const [module,    setModule]    = useState(null);
  const [tab,       setTab]       = useState('overview');
  const [toast,     setToast]     = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [ovForm,    setOvForm]    = useState({ title: '', overview: '' });
  const [qModal,           setQModal]           = useState(false);
  const [editingQuestion,  setEditingQuestion]  = useState(null);
  const [qForm,            setQForm]            = useState({ question_text: '', options: ['', '', '', ''], correct: 0 });
  const [qzForm,    setQzForm]    = useState({ quiz_passmark: 70, quiz_time_limit_minutes: 15, quiz_allow_retake: false, quiz_show_answers: false });
  const [renamingId,    setRenamingId]    = useState(null);
  const [renameValue,   setRenameValue]   = useState('');
  const [linkModal,     setLinkModal]     = useState(false);
  const [linkForm,      setLinkForm]      = useState({ name: '', url: '' });
  const [renamingSlide, setRenamingSlide] = useState(false);
  const [slideNameValue,setSlideNameValue]= useState('');
  const [renamingVideoId,  setRenamingVideoId]  = useState(null);
  const [videoNameValue,   setVideoNameValue]   = useState('');
  const [selectedVideoId,  setSelectedVideoId]  = useState(null);
  const [videoMaxMb,    setVideoMaxMb]    = useState(500);
  const [slideMaxMb,    setSlideMaxMb]    = useState(10);
  const [resourceMaxMb, setResourceMaxMb] = useState(50);

  const videoInputRef    = useRef(null);
  const slideInputRef    = useRef(null);
  const resourceInputRef = useRef(null);
  const [videoProgress,    setVideoProgress]    = useState(0);
  const [slideProgress,    setSlideProgress]    = useState(0);
  const [resourceProgress, setResourceProgress] = useState(0);
  const [videoUploading,    setVideoUploading]    = useState(false);
  const [slideUploading,    setSlideUploading]    = useState(false);
  const [resourceUploading, setResourceUploading] = useState(false);

  const load = useCallback(() => {
    getModule(Number(courseId), Number(moduleId)).then(d => {
      setModule(d.module);
      setOvForm({ title: d.module.title, overview: d.module.overview || '' });
      setQzForm({
        quiz_passmark:           d.module.quiz_passmark           ?? 70,
        quiz_time_limit_minutes: d.module.quiz_time_limit_minutes ?? 15,
        quiz_allow_retake:       d.module.quiz_allow_retake  === true,
        quiz_show_answers:       d.module.quiz_show_answers  === true,
      });
    }).catch(console.error);
  }, [courseId, moduleId]);

  useEffect(() => {
    load();
    getUploadSettings().then(s => {
      if (s.video_max_mb)        setVideoMaxMb(s.video_max_mb);
      if (s.presentation_max_mb) setSlideMaxMb(s.presentation_max_mb);
      if (s.resource_max_mb)     setResourceMaxMb(s.resource_max_mb);
    }).catch(() => {});
  }, [load]);

  async function handleSaveOverview(e) {
    e.preventDefault(); setSaving(true);
    try {
      await updateModule(Number(courseId), Number(moduleId), ovForm);
      load(); setToast({ msg: 'Module saved', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
    finally { setSaving(false); }
  }

  async function handlePublish() {
    const newStatus = module.status === 'published' ? 'draft' : 'published';
    setSaving(true);
    try {
      await updateModule(Number(courseId), Number(moduleId), { status: newStatus });
      load(); setToast({ msg: `Module ${newStatus === 'published' ? 'published' : 'set to draft'}`, type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
    finally { setSaving(false); }
  }

  async function handleSaveQuizSettings(e) {
    e.preventDefault(); setSaving(true);
    try {
      await updateModule(Number(courseId), Number(moduleId), qzForm);
      setToast({ msg: 'Quiz settings saved', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
    finally { setSaving(false); }
  }

  async function handleAddQuestion(e) {
    e.preventDefault(); setSaving(true);
    try {
      const options = qForm.options.filter(o => o.trim()).map((text, i) => ({ text, is_correct: i === qForm.correct }));
      if (options.length < 2) { setToast({ msg: 'Add at least 2 options', type: 'error' }); return; }
      await addQuestion(Number(courseId), Number(moduleId), { question_text: qForm.question_text, options });
      load(); setQModal(false); setQForm({ question_text: '', options: ['', '', '', ''], correct: 0 });
      setToast({ msg: 'Question added', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
    finally { setSaving(false); }
  }

  async function handleEditQuestion(e) {
    e.preventDefault(); setSaving(true);
    try {
      const options = qForm.options.filter(o => o.trim()).map((text, i) => ({ text, is_correct: i === qForm.correct }));
      if (options.length < 2) { setToast({ msg: 'Add at least 2 options', type: 'error' }); return; }
      await updateQuestion(Number(courseId), Number(moduleId), editingQuestion.id, { question_text: qForm.question_text, options });
      load(); setEditingQuestion(null); setQModal(false);
      setToast({ msg: 'Question updated', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
    finally { setSaving(false); }
  }

  async function handleDeleteQuestion(qId) {
    if (!window.confirm('Delete this question?')) return;
    try {
      await deleteQuestion(Number(courseId), Number(moduleId), qId);
      load(); setToast({ msg: 'Question deleted', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
  }

  async function handleDeleteResource(rId) {
    try {
      await deleteResource(Number(courseId), Number(moduleId), rId);
      load(); setToast({ msg: 'Resource removed', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
  }

  async function handleDeleteVideo(vId) {
    if (!window.confirm('Delete this video?')) return;
    try {
      await deleteVideo(Number(courseId), Number(moduleId), vId);
      load(); setToast({ msg: 'Video removed', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
  }

  async function handleRenameVideo(e, vId, origExt) {
    e.preventDefault();
    const baseName = videoNameValue.trim();
    if (!baseName) return;
    try {
      await renameVideo(Number(courseId), Number(moduleId), vId, baseName + origExt);
      load(); setRenamingVideoId(null);
      setToast({ msg: 'Video renamed', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
  }

  async function handleVideoFile(e) {
    const file = e.target.files[0]; if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['mp4', 'mov', 'webm', 'avi'].includes(ext)) {
      setToast({ msg: `Invalid file type .${ext}. Only MP4, MOV, WEBM, AVI are allowed.`, type: 'error' });
      e.target.value = ''; return;
    }
    if (file.size > videoMaxMb * 1024 * 1024) {
      setToast({ msg: `File too large. Maximum size is ${videoMaxMb} MB.`, type: 'error' });
      e.target.value = ''; return;
    }
    setVideoUploading(true); setVideoProgress(0);
    try {
      await uploadFile(courseId, moduleId, 'video', file, setVideoProgress);
      load(); setToast({ msg: 'Video uploaded successfully', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
    finally { setVideoUploading(false); setVideoProgress(0); e.target.value = ''; }
  }

  async function handleRemoveVideo() {
    try {
      await updateModule(Number(courseId), Number(moduleId), { video_uploaded: false, video_file_name: '', video_file_path: '' });
      load();
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
  }

  async function handleSlideFile(e) {
    const file = e.target.files[0]; if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pptx', 'ppt', 'pdf'].includes(ext)) {
      setToast({ msg: `Invalid file type .${ext}. Only PPTX, PPT, PDF are allowed.`, type: 'error' });
      e.target.value = ''; return;
    }
    if (file.size > slideMaxMb * 1024 * 1024) {
      setToast({ msg: `File too large. Maximum size is ${slideMaxMb} MB.`, type: 'error' });
      e.target.value = ''; return;
    }
    setSlideUploading(true); setSlideProgress(0);
    try {
      await uploadFile(courseId, moduleId, 'presentation', file, setSlideProgress);
      load(); setToast({ msg: 'Slides uploaded successfully', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
    finally { setSlideUploading(false); setSlideProgress(0); e.target.value = ''; }
  }

  async function handleRemovePresentation() {
    try {
      await updateModule(Number(courseId), Number(moduleId), { presentation_uploaded: false, presentation_file_name: '', presentation_file_path: '' });
      load();
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
  }

  async function handleResourceFile(e) {
    const file = e.target.files[0]; if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'zip', 'txt'].includes(ext)) {
      setToast({ msg: `Invalid file type .${ext}. Allowed: PDF, DOC, DOCX, XLS, XLSX, CSV, ZIP, TXT.`, type: 'error' });
      e.target.value = ''; return;
    }
    if (file.size > resourceMaxMb * 1024 * 1024) {
      setToast({ msg: `File too large. Maximum size is ${resourceMaxMb} MB.`, type: 'error' });
      e.target.value = ''; return;
    }
    setResourceUploading(true); setResourceProgress(0);
    try {
      await uploadFile(courseId, moduleId, 'resource', file, setResourceProgress);
      load(); setToast({ msg: 'Resource uploaded', type: 'success' });
    } catch (err) { setToast({ msg: err.message, type: 'error' }); }
    finally { setResourceUploading(false); setResourceProgress(0); e.target.value = ''; }
  }

  if (!module) return <div className="body" style={{ color: 'var(--muted)', padding: 40 }}>Loading…</div>;

  const questions = module.questions || [];
  const resources  = module.resources  || [];
  const videos     = module.videos     || [];

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Admin / Courses / Module Editor</div>
          <div className="pt">{module.title}</div>
          <div className="ps">Module editor · Upload content and manage quiz questions</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-o btn-sm" onClick={() => navigate(`/admin/courses/${courseId}?tab=modules`)}>
            ← Back
          </button>
          <button
            className={`btn btn-sm ${module.status === 'published' ? 'btn-g' : 'btn-p'}`}
            onClick={handlePublish}
            disabled={saving}
          >
            {module.status === 'published' ? '✓ Published' : 'Publish Module'}
          </button>
        </div>
      </div>

      <div className="body">
        {/* ── Tab Bar ── */}
        <div className="tab-bar">
          {TABS.map(t => (
            <button key={t.key} className={`tab-item${tab === t.key ? ' on' : ''}`} onClick={() => setTab(t.key)}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div style={{ maxWidth: 800 }}>

          {/* ── Overview ── */}
          {tab === 'overview' && (
            <>
              <div className="card">
                <div className="ct">Module Details</div>
                <form onSubmit={handleSaveOverview}>
                  <div className="fg">
                    <label className="fl">Module Title</label>
                    <input className="li" required value={ovForm.title}
                      onChange={e => setOvForm(f => ({ ...f, title: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label className="fl">Module Overview</label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={ovForm.overview || ''}
                      onChange={(_, editor) => setOvForm(f => ({ ...f, overview: editor.getData() }))}
                      config={{ toolbar: ['bold','italic','|','bulletedList','numberedList','|','blockQuote','link'] }}
                    />
                  </div>
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label className="fl">Status</label>
                    <select className="li" value={module.status} disabled={saving}
                      onChange={async e => {
                        const newStatus = e.target.value; setSaving(true);
                        try {
                          await updateModule(Number(courseId), Number(moduleId), { status: newStatus });
                          load(); setToast({ msg: `Status set to ${newStatus === 'published' ? 'Published' : 'Draft'}`, type: 'success' });
                        } catch (err) { setToast({ msg: err.message, type: 'error' }); }
                        finally { setSaving(false); }
                      }}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <button type="submit" className="btn btn-p btn-sm" disabled={saving}>
                      {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="card">
                <div className="ct">Content Checklist</div>
                {[
                  { icon: '🎬', label: 'Video Recording',     done: videos.length > 0 },
                  { icon: '📑', label: 'Presentation Slides', done: module.presentation_uploaded },
                  { icon: '📝', label: 'Quiz Questions',      done: questions.length > 0 },
                  { icon: '📎', label: 'Resources',           done: resources.length > 0 },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: i < 3 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 16 }}>{item.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{item.label}</span>
                    </div>
                    <span className={`b ${item.done ? 'b-g' : 'b-r'}`}>{item.done ? 'Ready' : 'Missing'}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Video ── */}
          {tab === 'video' && (() => {
            const videos = module.videos || [];
            const activeVideo = videos.find(v => v.id === selectedVideoId) || videos[0] || null;
            return (
              <div className="card">
                <input ref={videoInputRef} type="file" accept=".mp4,.mov,.webm,.avi" style={{ display: 'none' }} onChange={handleVideoFile} />

                <div className="ct">
                  <span>Video Recordings <span className="b b-y">{videos.length} video{videos.length !== 1 ? 's' : ''}</span></span>
                  {!videoUploading && (
                    <button className="btn btn-dark btn-sm" onClick={() => { videoInputRef.current.value = ''; videoInputRef.current.click(); }}>+ Add Video</button>
                  )}
                </div>

                {videoUploading && <UploadProgress label="Uploading video…" pct={videoProgress} icon="🎬" />}

                {videos.length === 0 && !videoUploading ? (
                  <div className="uz" onClick={() => { videoInputRef.current.value = ''; videoInputRef.current.click(); }}>
                    <div className="uz-icon">🎬</div>
                    <div className="uz-title">Drop your video here, or click to browse</div>
                    <div className="uz-sub">MP4, MOV, WEBM, AVI · Multiple files supported · Max {videoMaxMb} MB each</div>
                  </div>
                ) : (
                  videos.map((v, vi) => {
                    const isRenaming  = renamingVideoId === v.id;
                    const isSelected  = activeVideo && activeVideo.id === v.id;
                    const fname    = v.name || '';
                    const dotIdx   = fname.lastIndexOf('.');
                    const origExt  = dotIdx !== -1 ? fname.slice(dotIdx) : '';
                    const sizeMb   = v.file_size_bytes ? (v.file_size_bytes / (1024 * 1024)).toFixed(1) + ' MB' : '';
                    return (
                      <div
                        key={v.id}
                        className="uf"
                        onClick={() => { if (!isRenaming) setSelectedVideoId(v.id); }}
                        style={{
                          alignItems: 'center', cursor: isRenaming ? 'default' : 'pointer',
                          borderBottom: vi < videos.length - 1 ? '1px solid var(--border)' : 'none',
                          paddingBottom: vi < videos.length - 1 ? 12 : 0,
                          marginBottom:  vi < videos.length - 1 ? 12 : 0,
                          borderRadius: 8,
                          background: isSelected ? 'var(--yellow-soft)' : 'transparent',
                          padding: '8px 10px',
                          outline: isSelected ? '2px solid var(--yellow)' : 'none',
                        }}
                      >
                        <div className="uf-icon" style={{ background: isSelected ? 'var(--yellow)' : 'var(--yellow-soft)', color: isSelected ? '#fff' : undefined }}>🎬</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {isRenaming ? (
                            <form style={{ display: 'flex', gap: 6, alignItems: 'center' }} onSubmit={e => { e.stopPropagation(); handleRenameVideo(e, v.id, origExt); }}>
                              <div style={{ display: 'flex', alignItems: 'center', flex: 1, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden', background: '#fff' }}>
                                <input style={{ flex: 1, padding: '4px 8px', fontSize: 13, height: 32, border: 'none', outline: 'none' }}
                                  value={videoNameValue} onChange={e => setVideoNameValue(e.target.value)} autoFocus required
                                  onClick={e => e.stopPropagation()} />
                                {origExt && <span style={{ padding: '0 10px', fontSize: 12, color: 'var(--muted)', background: '#f5f5f5', height: '100%', display: 'flex', alignItems: 'center', borderLeft: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{origExt}</span>}
                              </div>
                              <button type="submit" className="btn btn-p btn-sm" onClick={e => e.stopPropagation()}>Save</button>
                              <button type="button" className="btn btn-o btn-sm" onClick={e => { e.stopPropagation(); setRenamingVideoId(null); }}>Cancel</button>
                            </form>
                          ) : (
                            <>
                              <div className="uf-name" style={{ fontWeight: isSelected ? 600 : undefined }}>{fname}</div>
                              {sizeMb && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{sizeMb}</div>}
                            </>
                          )}
                        </div>
                        {!isRenaming && (
                          <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                            <button className="btn btn-o btn-sm" onClick={() => { setVideoNameValue(dotIdx !== -1 ? fname.slice(0, dotIdx) : fname); setRenamingVideoId(v.id); }}>✏ Rename</button>
                            {v.file_path && <button className="btn btn-o btn-sm" onClick={() => downloadFile(v.file_path, fname)}>⬇ Download</button>}
                            <button className="uf-rm" onClick={() => handleDeleteVideo(v.id)}>✕</button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {activeVideo && !videoUploading && (
                  <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Preview — {activeVideo.name}
                    </div>
                    <video controls key={activeVideo.file_path} style={{ width: '100%', borderRadius: 10, background: '#000', maxHeight: 380, display: 'block' }}>
                      <source src={`/api/admin/uploads/${activeVideo.file_path}`} />
                      Your browser does not support video playback.
                    </video>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Slides ── */}
          {tab === 'presentation' && (
            <div className="card">
              <input ref={slideInputRef} type="file" accept=".pptx,.ppt,.pdf" style={{ display: 'none' }} onChange={handleSlideFile} />
              {module.presentation_uploaded ? (
                <>
                  <div className="ct"><span>Presentation Slides</span><span className="b b-g">✓ Uploaded</span></div>
                  <div className="uf" style={{ alignItems: 'center' }}>
                    <div className="uf-icon" style={{ background: 'var(--yellow-soft)' }}>📑</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {renamingSlide ? (() => {
                        const fname = module.presentation_file_name || '';
                        const dotIdx = fname.lastIndexOf('.');
                        const origExt = dotIdx !== -1 ? fname.slice(dotIdx) : '';
                        return (
                          <form style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                            onSubmit={async e => {
                              e.preventDefault();
                              const baseName = slideNameValue.trim(); if (!baseName) return;
                              try {
                                await updateModule(Number(courseId), Number(moduleId), { presentation_file_name: baseName + origExt });
                                load(); setToast({ msg: 'Presentation renamed', type: 'success' });
                              } catch (err) { setToast({ msg: err.message, type: 'error' }); }
                              finally { setRenamingSlide(false); }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden', background: '#fff' }}>
                              <input style={{ flex: 1, padding: '4px 8px', fontSize: 13, height: 32, border: 'none', outline: 'none' }}
                                value={slideNameValue} onChange={e => setSlideNameValue(e.target.value)} autoFocus required />
                              {origExt && <span style={{ padding: '0 10px', fontSize: 12, color: 'var(--muted)', background: '#f5f5f5', height: '100%', display: 'flex', alignItems: 'center', borderLeft: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{origExt}</span>}
                            </div>
                            <button type="submit" className="btn btn-p btn-sm">Save</button>
                            <button type="button" className="btn btn-o btn-sm" onClick={() => setRenamingSlide(false)}>Cancel</button>
                          </form>
                        );
                      })() : <div className="uf-name">{module.presentation_file_name || 'presentation_slides.pptx'}</div>}
                    </div>
                    {!renamingSlide && (<>
                      <button className="btn btn-o btn-sm" onClick={() => { const f = module.presentation_file_name || ''; const d = f.lastIndexOf('.'); setSlideNameValue(d !== -1 ? f.slice(0, d) : f); setRenamingSlide(true); }}>✏ Rename</button>
                      {module.presentation_file_path && <button className="btn btn-o btn-sm" onClick={() => downloadFile(module.presentation_file_path, module.presentation_file_name)}>⬇ Download</button>}
                      <button className="btn btn-o btn-sm" onClick={() => slideInputRef.current.click()}>↺ Replace</button>
                      <button className="uf-rm" onClick={handleRemovePresentation}>✕</button>
                    </>)}
                  </div>
                </>
              ) : slideUploading ? (
                <UploadProgress label="Uploading slides…" pct={slideProgress} icon="📑" />
              ) : (
                <>
                  <div className="ct">Upload Presentation</div>
                  <div className="uz" onClick={() => slideInputRef.current.click()}>
                    <div className="uz-icon">📑</div>
                    <div className="uz-title">Drop your presentation here, or click to browse</div>
                    <div className="uz-sub">PPTX, PPT, PDF · Single file · Max {slideMaxMb} MB</div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Quiz Builder ── */}
          {tab === 'quiz' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700, fontSize: 14 }}>
                  Quiz Questions <span className="b b-y">{questions.length} questions</span>
                </div>
                <button className="btn btn-dark btn-sm" onClick={() => { setQForm({ question_text: '', options: ['', '', '', ''], correct: 0 }); setEditingQuestion(null); setQModal(true); }}>
                  + Add Question
                </button>
              </div>

              {questions.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 44 }}>
                  <div style={{ fontSize: 44, marginBottom: 14 }}>📝</div>
                  <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No quiz questions yet</div>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, maxWidth: 320, margin: '0 auto 18px' }}>Add questions for students to test their understanding.</p>
                  <button className="btn btn-dark btn-sm" onClick={() => { setQForm({ question_text: '', options: ['', '', '', ''], correct: 0 }); setEditingQuestion(null); setQModal(true); }}>+ Add First Question</button>
                </div>
              ) : (
                questions.map((q, qi) => (
                  <div key={q.id} className="card" style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                      <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700, fontSize: 13.5 }}>Q{qi + 1}. {q.question_text}</div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button className="btn btn-o btn-sm" onClick={() => {
                          const opts = q.options.map(o => o.text);
                          while (opts.length < 4) opts.push('');
                          const correctIdx = q.options.findIndex(o => o.is_correct);
                          setQForm({ question_text: q.question_text, options: opts, correct: correctIdx >= 0 ? correctIdx : 0 });
                          setEditingQuestion(q); setQModal(true);
                        }}>✏ Edit</button>
                        <button className="btn btn-d btn-sm" onClick={() => handleDeleteQuestion(q.id)}>✕</button>
                      </div>
                    </div>
                    {q.options.map((o, oi) => (
                      <div key={o.id ?? oi} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 'var(--rs)', marginBottom: 4, background: o.is_correct ? 'var(--green-soft)' : 'transparent', border: o.is_correct ? '1px solid var(--green-border)' : '1px solid transparent' }}>
                        <span style={{ fontSize: 12 }}>{o.is_correct ? '✓' : '○'}</span>
                        <span style={{ fontSize: 12.5 }}>{o.text}</span>
                      </div>
                    ))}
                  </div>
                ))
              )}

              <div className="card" style={{ marginTop: 14 }}>
                <div className="ct">Quiz Settings</div>
                <form onSubmit={handleSaveQuizSettings}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="fg">
                      <label className="fl">Pass Mark (%)</label>
                      <input className="li" type="number" min="0" max="100" value={qzForm.quiz_passmark}
                        onChange={e => setQzForm(f => ({ ...f, quiz_passmark: Number(e.target.value) }))} />
                    </div>
                    <div className="fg">
                      <label className="fl">Time Limit (min)</label>
                      <input className="li" type="number" min="1" value={qzForm.quiz_time_limit_minutes}
                        onChange={e => setQzForm(f => ({ ...f, quiz_time_limit_minutes: Number(e.target.value) }))} />
                    </div>
                  </div>
                  <div className="fg">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={qzForm.quiz_allow_retake} style={{ accentColor: 'var(--yellow)' }}
                        onChange={e => setQzForm(f => ({ ...f, quiz_allow_retake: e.target.checked }))} />
                      Allow students to retake quiz
                    </label>
                  </div>
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={qzForm.quiz_show_answers} style={{ accentColor: 'var(--yellow)' }}
                        onChange={e => setQzForm(f => ({ ...f, quiz_show_answers: e.target.checked }))} />
                      Show correct answers to students after submission
                    </label>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <button type="submit" className="btn btn-p btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save Settings'}</button>
                  </div>
                </form>
              </div>
            </>
          )}

          {/* ── Resources ── */}
          {tab === 'resources' && (
            <div className="card">
              <div className="ct">
                <span>Module Resources <span className="b b-y">{resources.length}</span></span>
                <button className="btn btn-o btn-sm" onClick={() => { setLinkForm({ name: '', url: '' }); setLinkModal(true); }}>🔗 Add Link</button>
              </div>

              {resources.map(r => {
                const isLink     = r.resource_type === 'link';
                const iconBg     = isLink ? 'var(--yellow-soft)' : r.resource_type === 'pdf' ? 'var(--orange-soft)' : r.resource_type === 'xls' ? 'var(--green-soft)' : 'var(--yellow-soft)';
                const icon       = isLink ? '🔗' : r.resource_type === 'pdf' ? '📄' : r.resource_type === 'xls' ? '📊' : '📝';
                const isRenaming = renamingId === r.id;
                return (
                  <div key={r.id} className="uf" style={{ alignItems: 'center' }}>
                    <div className="uf-icon" style={{ background: iconBg }}>{icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isRenaming ? (() => {
                        const dotIdx = r.name.lastIndexOf('.');
                        const origExt = !isLink && dotIdx !== -1 ? r.name.slice(dotIdx) : '';
                        return (
                          <form style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                            onSubmit={async e => {
                              e.preventDefault();
                              const baseName = renameValue.trim(); if (!baseName) return;
                              try {
                                await renameResource(courseId, moduleId, r.id, baseName + origExt);
                                load(); setToast({ msg: 'Renamed', type: 'success' });
                              } catch (err) { setToast({ msg: err.message, type: 'error' }); }
                              finally { setRenamingId(null); }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1, border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden', background: '#fff' }}>
                              <input style={{ flex: 1, padding: '4px 8px', fontSize: 13, height: 32, border: 'none', outline: 'none' }}
                                value={renameValue} onChange={e => setRenameValue(e.target.value)} autoFocus required />
                              {origExt && <span style={{ padding: '0 10px', fontSize: 12, color: 'var(--muted)', background: '#f5f5f5', height: '100%', display: 'flex', alignItems: 'center', borderLeft: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{origExt}</span>}
                            </div>
                            <button type="submit" className="btn btn-p btn-sm">Save</button>
                            <button type="button" className="btn btn-o btn-sm" onClick={() => setRenamingId(null)}>Cancel</button>
                          </form>
                        );
                      })() : (
                        <>
                          <div className="uf-name">{r.name}</div>
                          {isLink && r.link_url && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.link_url}</div>}
                          {!isLink && r.size && <div className="uf-size">{r.size}</div>}
                        </>
                      )}
                    </div>
                    {!isRenaming && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-o btn-sm" onClick={() => { const d = r.name.lastIndexOf('.'); setRenamingId(r.id); setRenameValue(!isLink && d !== -1 ? r.name.slice(0, d) : r.name); }}>✏ Rename</button>
                        {isLink && r.link_url
                          ? <a href={r.link_url} target="_blank" rel="noreferrer"
                              style={{ fontSize: 12, color: '#1A56DB', fontWeight: 600, padding: '5px 11px', background: '#E8F0FE', borderRadius: 6, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              ↗ Open
                            </a>
                          : r.file_path && <button className="btn btn-o btn-sm" onClick={() => downloadFile(r.file_path, r.name)}>⬇ Download</button>
                        }
                        <button className="uf-rm" onClick={() => handleDeleteResource(r.id)}>✕</button>
                      </div>
                    )}
                  </div>
                );
              })}

              <input ref={resourceInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,.txt" style={{ display: 'none' }} onChange={handleResourceFile} />
              {resourceUploading ? (
                <UploadProgress label="Uploading resource…" pct={resourceProgress} icon="📎" />
              ) : (
                <div className="uz" style={{ marginTop: 12, padding: '28px 20px' }} onClick={() => { resourceInputRef.current.value = ''; resourceInputRef.current.click(); }}>
                  <div className="uz-icon" style={{ width: 40, height: 40, fontSize: 18 }}>📎</div>
                  <div className="uz-title">Upload a resource file</div>
                  <div className="uz-sub">PDF, DOCX, XLSX, CSV, ZIP · Max {resourceMaxMb} MB each</div>
                </div>
              )}
            </div>
          )}

          {/* ── Add Link Modal ── */}
          {linkModal && (
            <div className="modal-overlay" onClick={() => setLinkModal(false)}>
              <div className="modal-box" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
                <div className="modal-title">
                  Add Link Resource
                  <button className="btn btn-o btn-sm" onClick={() => setLinkModal(false)}>✕</button>
                </div>
                <form onSubmit={async e => {
                  e.preventDefault();
                  const name = linkForm.name.trim();
                  const url  = linkForm.url.trim();
                  if (!name || !url) return;
                  try {
                    await addLinkResource(Number(courseId), Number(moduleId), name, url);
                    load(); setLinkModal(false); setToast({ msg: 'Link added', type: 'success' });
                  } catch (err) { setToast({ msg: err.message, type: 'error' }); }
                }}>
                  <div className="fg">
                    <label className="fl">Label</label>
                    <input className="li" required placeholder="e.g. Course Reference"
                      value={linkForm.name} onChange={e => setLinkForm(f => ({ ...f, name: e.target.value }))} autoFocus />
                  </div>
                  <div className="fg">
                    <label className="fl">URL</label>
                    <input className="li" required type="url" placeholder="https://…"
                      value={linkForm.url} onChange={e => setLinkForm(f => ({ ...f, url: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                    <button type="button" className="btn btn-o btn-sm" onClick={() => setLinkModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-p btn-sm">Add Link</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Question Modal ── */}
      {qModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              {editingQuestion ? 'Edit Quiz Question' : 'Add Quiz Question'}
              <button className="btn btn-o btn-sm" onClick={() => { setQModal(false); setEditingQuestion(null); }}>✕</button>
            </div>
            <form onSubmit={editingQuestion ? handleEditQuestion : handleAddQuestion}>
              <div className="fg">
                <label className="fl">Question</label>
                <textarea className="li" required placeholder="Enter your question…"
                  value={qForm.question_text} onChange={e => setQForm(f => ({ ...f, question_text: e.target.value }))} />
              </div>
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="fl">Answer Options <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(select the correct one)</span></label>
                {qForm.options.map((opt, i) => (
                  <div key={i} className="qb-opt">
                    <input type="radio" className="qb-radio" name="correct" checked={qForm.correct === i}
                      onChange={() => setQForm(f => ({ ...f, correct: i }))} />
                    <input className="qb-input" placeholder={`Option ${i + 1}`} value={opt}
                      onChange={e => { const opts = [...qForm.options]; opts[i] = e.target.value; setQForm(f => ({ ...f, options: opts })); }} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button type="button" className="btn btn-o" onClick={() => { setQModal(false); setEditingQuestion(null); }}>Cancel</button>
                <button type="submit" className="btn btn-p" disabled={saving}>
                  {saving ? (editingQuestion ? 'Saving…' : 'Adding…') : (editingQuestion ? 'Save Changes' : 'Add Question')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </>
  );
}
